/**
 * コードタブのファイルツリーを、ZIP内のパスの一覧から組み立てる。
 *
 * パスは "server/tasks/register_request.py" のように "/" 区切りで届くので、
 * フォルダを1段ずつの入れ子にし、IDEのエクスプローラと同じ並びにそろえる。
 * 開閉のような表示の状態はここでは持たない（CodeTab側の関心）。
 */

/** ZIPの入口。manifestの位置は登録契約で固定されている。 */
export const MANIFEST = 'manifest.json';

export interface FileNode {
  kind: 'file';
  /** 表示に使う末尾の名前。 */
  name: string;
  /** ZIP内のフルパス。選択・URL・読み上げにはこちらを使う。 */
  path: string;
}

export interface FolderNode {
  kind: 'folder';
  name: string;
  /** ルートからのフォルダのパス（末尾の "/" なし）。開閉の状態はこれを鍵に持つ。 */
  path: string;
  children: TreeNode[];
}

export type TreeNode = FileNode | FolderNode;

/**
 * パスの一覧を入れ子のツリーにする。
 *
 * 並び順:
 * - ルート直下は manifest.json を先頭に固定し、次にフォルダ、最後に残りのファイル。
 *   「フォルダが先」の規則どおりに置くと、アプリの入口が全フォルダの後ろ（一覧の
 *   末尾）に埋もれてしまうため、入口だけは例外にする。
 * - フォルダの中はサブフォルダが先、ファイルが後。それぞれ名前の昇順。
 */
export function buildFileTree(paths: readonly string[]): TreeNode[] {
  const root = draftFolder('', '');
  for (const path of new Set(paths)) {
    const segments = segmentsOf(path);
    const name = segments.pop();
    if (name === undefined) continue;
    let folder = root;
    for (const segment of segments) {
      let child = folder.folders.get(segment);
      if (!child) {
        child = draftFolder(segment, folder.path ? `${folder.path}/${segment}` : segment);
        folder.folders.set(segment, child);
      }
      folder = child;
    }
    folder.files.push({ kind: 'file', name, path });
  }

  const { folders, files } = sortedChildren(root);
  const entry = files.filter((file) => file.name === MANIFEST);
  const others = files.filter((file) => file.name !== MANIFEST);
  return [...entry, ...folders, ...others];
}

/**
 * ファイルを含むフォルダを外側から順に返す。
 * "server/api/requests.py" → ["server", "server/api"]。鍵は FolderNode.path と同じ形。
 */
export function ancestorFolders(path: string): string[] {
  const folders = segmentsOf(path).slice(0, -1);
  return folders.map((_, index) => folders.slice(0, index + 1).join('/'));
}

/** 組み立て途中のフォルダ。同名の子を1つにまとめるため Map で持つ。 */
interface DraftFolder {
  name: string;
  path: string;
  folders: Map<string, DraftFolder>;
  files: FileNode[];
}

function draftFolder(name: string, path: string): DraftFolder {
  return { name, path, folders: new Map(), files: [] };
}

function sortedChildren(draft: DraftFolder): { folders: FolderNode[]; files: FileNode[] } {
  const folders = [...draft.folders.values()].sort(byName).map((folder): FolderNode => {
    const children = sortedChildren(folder);
    return {
      kind: 'folder',
      name: folder.name,
      path: folder.path,
      children: [...children.folders, ...children.files],
    };
  });
  return { folders, files: [...draft.files].sort(byName) };
}

/** 空の区切り（"a//b" や末尾の "/"）は段として数えない。 */
function segmentsOf(path: string): string[] {
  return path.split('/').filter((segment) => segment !== '');
}

/**
 * 名前の昇順。VS Codeのエクスプローラと同じく数字は数として比べる（task2 < task10）。
 * 実行環境のロケールで順が変わらないよう言語を固定し、照合で同順になったときは
 * コード順で決めて並びを安定させる。
 */
const collator = new Intl.Collator('en', { numeric: true });

function byName(left: { name: string }, right: { name: string }): number {
  const order = collator.compare(left.name, right.name);
  if (order !== 0) return order;
  if (left.name === right.name) return 0;
  return left.name < right.name ? -1 : 1;
}
